/**
 * Closing the laptop mid-run (Sofia, Nov 2 2026): did the machine sleep, or the network drop, while a
 * step of a run was going? A heartbeat every second; a gap far longer than that means nothing ran — the
 * Mac slept. `online` / `offline` / `visibilitychange` are listened to as well. The runs ask
 * `disturbedSince(stepStart)` when a step fails, and `waitUntilOnline()` before they try again.
 *
 * A HIDDEN tab is throttled by Chrome (after 5 minutes hidden: one timer wake-up per minute), so a
 * hidden page needs a much longer gap before it counts as sleep — otherwise working in another tab
 * would look like a sleep every minute.
 */
import { useEffect, useState } from 'react';

export const TICK_MS = 1_000;
/** A gap this long while the page was visible = the machine slept. */
export const SLEEP_GAP_MS = 20_000;
/** …and while it was hidden (Chrome's intensive throttling runs timers once a minute). */
export const SLEEP_GAP_HIDDEN_MS = 90_000;
/** Back online: a few seconds for Wi-Fi, DNS and the extension's service worker to settle. */
export const SETTLE_MS = 3_000;

export interface WakeDetector {
  /** Run the heartbeat check now (a failure can arrive before the next tick saw the gap). */
  check: () => void;
  /** The machine woke from sleep at or after `t`. */
  sleptSince: (t: number) => boolean;
  /** Slept, or was offline, at or after `t` — or is offline right now. */
  disturbedSince: (t: number) => boolean;
  /** Resolves once the browser says online, then `settleMs` later — or at once on `cancelWaits()`. */
  waitUntilOnline: (settleMs?: number) => Promise<void>;
  /** Release every pending `waitUntilOnline` now (Stop, unmount); the caller checks its own stop flag next. */
  cancelWaits: () => void;
  /** Called on every detected wake (with how long it slept). Returns the unsubscribe. */
  onWake: (fn: (sleptMs: number) => void) => () => void;
  start: () => void;
  stop: () => void;
}

const isOnline = () => (typeof navigator === 'undefined' ? true : navigator.onLine !== false);
const isHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

export function createWakeDetector({ tickMs = TICK_MS, gapMs = SLEEP_GAP_MS, hiddenGapMs = SLEEP_GAP_HIDDEN_MS } = {}): WakeDetector {
  let last = Date.now();
  let hiddenAtLast = isHidden();
  let wokeAt = -Infinity;
  let offlineAt = isOnline() ? -Infinity : Date.now();
  let timer: ReturnType<typeof setInterval> | null = null;
  const wakeFns = new Set<(sleptMs: number) => void>();
  const onlineFns = new Set<() => void>();
  const cancelFns = new Set<() => void>();
  const cancelWaits = () => {
    for (const fn of [...cancelFns]) fn();
  };

  function check() {
    const t = Date.now();
    const gap = t - last;
    if (gap > (hiddenAtLast ? hiddenGapMs : gapMs)) {
      wokeAt = t;
      for (const fn of [...wakeFns]) fn(gap);
    }
    last = t;
    hiddenAtLast = isHidden();
  }
  const onOffline = () => {
    offlineAt = Date.now();
  };
  const onOnline = () => {
    check();
    for (const fn of [...onlineFns]) fn();
  };
  const onVisibility = () => check();

  return {
    check,
    sleptSince: (t) => wokeAt >= t,
    disturbedSince: (t) => wokeAt >= t || offlineAt >= t || !isOnline(),
    waitUntilOnline: (settleMs = SETTLE_MS) =>
      new Promise<void>((resolve) => {
        let t: ReturnType<typeof setTimeout> | null = null;
        const done = () => {
          onlineFns.delete(fn);
          cancelFns.delete(cancel);
          resolve();
        };
        const settle = () => {
          onlineFns.delete(fn);
          t = setTimeout(done, settleMs);
        };
        const fn = () => settle();
        const cancel = () => {
          if (t) clearTimeout(t);
          done();
        };
        cancelFns.add(cancel);
        if (isOnline()) return void settle();
        onlineFns.add(fn);
      }),
    cancelWaits,
    onWake: (fn) => {
      wakeFns.add(fn);
      return () => void wakeFns.delete(fn);
    },
    start() {
      if (timer) return;
      last = Date.now();
      hiddenAtLast = isHidden();
      timer = setInterval(check, tickMs);
      globalThis.addEventListener?.('offline', onOffline);
      globalThis.addEventListener?.('online', onOnline);
      globalThis.document?.addEventListener('visibilitychange', onVisibility);
    },
    stop() {
      if (timer) clearInterval(timer);
      timer = null;
      // Unmounted: a run waiting for the network must still reach its finally (CodeRabbit, PR #783).
      cancelWaits();
      globalThis.removeEventListener?.('offline', onOffline);
      globalThis.removeEventListener?.('online', onOnline);
      globalThis.document?.removeEventListener('visibilitychange', onVisibility);
    },
  };
}

/** One detector for the component's lifetime, ticking while it is mounted. `given` (tests) is used instead. */
export function useWakeDetector(given?: WakeDetector): WakeDetector {
  const [own] = useState(createWakeDetector);
  useEffect(() => {
    if (given) return;
    own.start();
    return () => own.stop();
  }, [given, own]);
  return given || own;
}
